/**
 * Maps gameplay events to sound file paths under `/sounds/`.
 *
 * Folder structure discovered by inventorying the decoded WAV tree:
 *   level1/   command-post overview (bgrn, factory, messages, save_game, stop_game,
 *             unit-type names, clock, print)
 *   level2/   factory submenu sounds (status, press, not_enough, not_minerals, per-unit cost)
 *   level4/   TRUCK driver (truck_mov*, mov_to_factory, mov_to_workers, load, unload, wait_minerals)
 *   level5/   SOLDIER driver (directions, grenade_discharger, rocket_louncher, weapons,
 *             beside_mark, succ/success, signal)
 *   level6/   ELECTRONIC GUN driver (gun_shot, low_p, middle_p, high_p, large_d,
 *             middle_d, move_gun)
 *   level7/   ROCKET PLANT driver (dist1-3, height1-3, miss1, destroy_ship)
 *   level8/   SPACE TANK driver (moving, laser_gun, photon, missil_shot, tank,
 *             grenade_launcher, shield)
 *   level9/   SPACE SCOUT driver (action, patrol, continue, continues_patrol,
 *             continues_spy, scout, enemy_cruiser, enemy_scout)
 *   level10/  SPACE CRUISER driver (gravitation, electronic_gun, photon, laser_gun,
 *             cruiser, missil_shot, radar1/2, shield)
 *   level11/  SHARED MESSAGES (NEW_MSG, OLD_MSG, anihilated, enemy_*, my_*,
 *             has_destroyed, has_found, lost_workers_trucks, lost_minerals,
 *             game_loaded, game_saved)
 *   menu/     main-menu navigation + bgrn
 *   game/     pause / save / victory / defeat / not_enough / configure
 *   difficulty/  difficulty selection sounds
 *   score/    "you have N points" digit + word readouts
 *   levels/   difficulty digit readouts (1.wav..7.wav)
 *
 * Workers have no driver folder — the player gets status from level1 + level11.
 */

import { UnitKind } from "@galaxy-ranger/core";

const BASE = "/sounds";

function driverFolder(kind: UnitKind): string {
  switch (kind) {
    case UnitKind.Worker: return "level1"; // worker is autonomous; reuse roster folder
    case UnitKind.Truck: return "level4";
    case UnitKind.Soldier: return "level5";
    case UnitKind.ElectronicGun: return "level6";
    case UnitKind.RocketPlant: return "level7";
    case UnitKind.SpaceTank: return "level8";
    case UnitKind.SpaceScout: return "level9";
    case UnitKind.SpaceCruiser: return "level10";
  }
}

/**
 * Translate an event-name to the actual filename inside a driver folder.
 * Some folders use different conventions (e.g. soldier's `level5` uses
 * `Shield.wav` with capital S; tank's `level8` uses lower-case `shield.wav`).
 * We default to the most common spelling and fall through silently when the
 * file is absent.
 */
/**
 * Per-folder remap from logical event names to actual filenames.
 *
 * Folders use inconsistent spellings (`Shield.wav` vs `shield.wav`,
 * `succ1.wav` vs `suc1.wav`, `radar1.wav` for the *search* radar and
 * `radar2.wav` for the *target* radar, etc.). This table is the single
 * place to align them.
 */
const REMAP: Partial<Record<UnitKind, Record<string, string>>> = {
  [UnitKind.Soldier]: {
    // Soldier (level5) — full kit. Shield uses capital S.
    shield: "Shield",
    energetic_shield: "Shield",
    moving: "steps",
    s_radar: "radar1",
    t_radar: "radar2",
    hit: "success1",
    kill: "success3",
    not: "not",
    // Incoming-shell: the enemy is firing at us with what would damage us.
    laser_shell: "laser_gun",
    electronic_shell: "robot_gun",
    bgrn: "bgrn",
  },
  [UnitKind.SpaceTank]: {
    // Tank (level8) — short firing samples are bare nouns.
    shield: "shield",
    energetic_shield: "shield",
    moving: "moving",
    s_radar: "radar1",
    t_radar: "radar2",
    hit: "suc1",
    kill: "suc3",
    not: "not",
    laser_shell: "laser",
    electronic_shell: "missil_shot",
    bgrn: "bgrn",
  },
  [UnitKind.SpaceCruiser]: {
    // Cruiser (level10) — uppercase short samples.
    shield: "shield",
    energetic_shield: "shield",
    s_radar: "radar1",
    t_radar: "radar2",
    hit: "missil_shot",
    kill: "missil_shot",
    not: "not",
    electronic: "electronic",
    photon: "PHOTON",
    laser: "LASER",
    gravitation_torpido: "gravitation",
    electronic_shell: "electronic",
    laser_shell: "LASER",
    bgrn: "bgrn",
  },
  [UnitKind.ElectronicGun]: {
    // Electronic gun (level6).
    shield: "Shield",
    energetic_shield: "Shield",
    moving: "move_gun",
    s_radar: "radar1",
    t_radar: "radar2",
    electronic_shell: "ef1",
    not: "not",
    bgrn: "bgrn",
  },
  [UnitKind.RocketPlant]: {
    // Rocket plant (level7) — uses bgrn1, not bgrn.
    shield: "Shield",
    energetic_shield: "Shield",
    moving: "move_gun",
    s_radar: "radar1",
    t_radar: "radar2",
    electronic_shell: "ef1",
    not: "not",
    missil_shot: "miss1", // plant's missile firing sample
    bgrn: "bgrn1",
  },
  [UnitKind.SpaceScout]: {
    // Scout (level9) — no weapons; ef1 stands in for incoming.
    shield: "shield",
    energetic_shield: "shield",
    s_radar: "radar1",
    moving: "moving",
    gravitation_torpido: "ef1",
    bgrn: "bgrn",
  },
  [UnitKind.Truck]: {
    moving: "truck_mov",
    bgrn: "bgrn",
  },
  [UnitKind.Worker]: {
    moving: "minerals",
    bgrn: "bgrn",
  },
};

function driverFile(kind: UnitKind, event: string): string {
  const folder = driverFolder(kind);
  const remapped = REMAP[kind]?.[event] ?? event;
  return `${BASE}/${folder}/${remapped}.wav`;
}

/** Public: per-driver sound URL. */
export function driverSound(kind: UnitKind, event: string): string {
  return driverFile(kind, event);
}

/**
 * Background ambient loop URL for `kind`'s driver mode. Every per-kind
 * folder ships a `bgrn.wav` (except the rocket plant, which uses `bgrn1`).
 * The remap table above carries the per-kind override.
 */
export function driverAmbient(kind: UnitKind): string {
  return driverFile(kind, "bgrn");
}

/** Top-level sounds that aren't tied to a particular driver. */
export const SOUNDS = {
  // Main menu navigation
  menuArrows: `${BASE}/menu/ARROWS.wav`,
  startGame: `${BASE}/menu/start_game.wav`,
  playWithPartner: `${BASE}/menu/play_with_partner.wav`,
  learnGameSounds: `${BASE}/menu/learn_game_sounds.wav`,
  finalScore: `${BASE}/menu/final_score.wav`,
  sendScore: `${BASE}/menu/send_score.wav`,
  testSpeakers: `${BASE}/menu/test_speakers.wav`,
  getId: `${BASE}/menu/get_id.wav`,
  quit: `${BASE}/menu/QUIT.wav`,
  bgMenu: `${BASE}/menu/BGRN.wav`,

  // Difficulty
  diffArrows: `${BASE}/difficulty/difficulty_arrows.wav`,
  // Speaker-test samples — pan-localised samples for verifying audio setup.
  testFrontLeft: `${BASE}/testspk/F_LEFT.wav`,
  testFrontRight: `${BASE}/testspk/F_RIGHT.wav`,
  diffClock: `${BASE}/difficulty/clock.wav`,
  diffEasy: `${BASE}/difficulty/EASY.wav`,
  diffMiddle: `${BASE}/difficulty/MIDDLE.wav`,
  diffHard: `${BASE}/difficulty/HARD.wav`,
  diffSuper: `${BASE}/difficulty/super.wav`,
  diffInsane: `${BASE}/difficulty/HARD1.wav`,

  // Command post (level1)
  cmdPost: `${BASE}/level1/command_post.wav`,
  cmdFactory: `${BASE}/level1/factory.wav`,
  cmdMessages: `${BASE}/level1/messages.wav`,
  cmdMinerals: `${BASE}/level1/minerals.wav`,
  cmdWorkers: `${BASE}/level1/workers.wav`,
  cmdTrucks: `${BASE}/level1/trucks.wav`,
  cmdSoldiers: `${BASE}/level1/soldiers.wav`,
  cmdLaserGuns: `${BASE}/level1/laser_guns.wav`,
  cmdMissiles: `${BASE}/level1/missiles.wav`,
  cmdSpaceTanks: `${BASE}/level1/space_tanks.wav`,
  cmdSpaceScouts: `${BASE}/level1/scout_ships.wav`,
  cmdSpaceCruisers: `${BASE}/level1/space_cruisers.wav`,
  cmdSaveGame: `${BASE}/level1/save_game.wav`,
  cmdStopGame: `${BASE}/level1/stop_game.wav`,
  bgGame: `${BASE}/level1/bgrn.wav`,
  bgGame2: `${BASE}/level1/bgrn2.wav`,
  clock: `${BASE}/level1/clock.wav`,
  print: `${BASE}/level1/print.wav`,

  // Factory submenu (level2) — spoken samples for each item.
  factoryStatus: `${BASE}/level2/status.wav`,
  factoryWorker: `${BASE}/level2/worker.wav`,
  factoryTruck: `${BASE}/level2/truck.wav`,
  factorySoldier: `${BASE}/level2/soldier.wav`,
  factoryGun: `${BASE}/level2/laser_gun.wav`,
  factoryPlant: `${BASE}/level2/missile.wav`,
  factoryTank: `${BASE}/level2/space_tank.wav`,
  factoryScout: `${BASE}/level2/scout_ship.wav`,
  factoryCruiser: `${BASE}/level2/military_ship.wav`,
  factoryPress: `${BASE}/level2/press.wav`,
  factoryNotEnough: `${BASE}/level2/not_enough.wav`,
  factoryNotMinerals: `${BASE}/level2/not_minerals.wav`,

  // Game state (game/)
  intro: `${BASE}/game/game_intro.wav`,
  pause: `${BASE}/game/pause.wav`,
  exitGame: `${BASE}/game/exit_game.wav`,
  notEnough: `${BASE}/game/not_enough.wav`,
  saveGame: `${BASE}/game/save_game.wav`,
  gameOver: `${BASE}/game/say_game_over.wav`,
  victory1: `${BASE}/game/victory1.wav`,
  victory2: `${BASE}/game/victory2.wav`,

  // Shared messages (level11)
  newMessage: `${BASE}/level11/NEW_MSG.wav`,
  oldMessage: `${BASE}/level11/OLD_MSG.wav`,
  enemyBase: `${BASE}/level11/enemy_base.wav`,
  hasDestroyed: `${BASE}/level11/has_destroyed.wav`,
  hasFound: `${BASE}/level11/has_found.wav`,
} as const;

/** Enemy-identification sounds when search radar finds something. */
export function enemyIdSound(kind: UnitKind): string {
  const names: Record<UnitKind, string> = {
    [UnitKind.Worker]: "enemy_soldier",
    [UnitKind.Truck]: "enemy_soldier",
    [UnitKind.Soldier]: "enemy_soldier",
    [UnitKind.ElectronicGun]: "enemy_gun",
    [UnitKind.RocketPlant]: "enemy_gun",
    [UnitKind.SpaceTank]: "enemy_tank",
    [UnitKind.SpaceScout]: "enemy_ship",
    [UnitKind.SpaceCruiser]: "enemy_ship",
  };
  return `${BASE}/level11/${names[kind]}.wav`;
}

export const NEW_MSG_SOUND = SOUNDS.newMessage;
